/**
 * Phonics Progress Service
 * Manages user progress for phonics learning
 */

export interface PhonicsProgress {
  userId: string;
  ruleProgress: Record<string, RuleProgress>;
  categoryProgress: Record<string, CategoryProgress>;
  totalPoints: number;
  totalStars: number;
  currentStreak: number;
  longestStreak: number;
  earnedBadges: PhonicsBadge[];
  lastPracticeDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface RuleProgress {
  ruleId: string;
  status: 'locked' | 'unlocked' | 'in-progress' | 'completed' | 'mastered';
  stars: 0 | 1 | 2 | 3;
  gameScores: {
    soundToShape: number;
    quickJudgment: number;
    spelling: number;
  };
  practiceCount: number;
  correctCount: number;
  lastPracticed: string;
  completedAt?: string;
}

export interface CategoryProgress {
  categoryId: string;
  rulesCompleted: number;
  totalRules: number;
  totalStars: number;
  maxStars: number;
  isUnlocked: boolean;
}

export interface PhonicsBadge {
  id: string;
  name: string;
  icon: string;
  description: string;
  earnedAt: string;
  category: 'mastery' | 'streak' | 'speed' | 'accuracy' | 'special';
}

export interface GameResult {
  gameType: 'soundToShape' | 'quickJudgment' | 'spelling';
  score: number;
  correct: number;
  total: number;
  accuracy: number;
}

const STORAGE_KEY = 'phonics_progress';

// Default progress template
const createDefaultProgress = (userId: string): PhonicsProgress => ({
  userId,
  ruleProgress: {},
  categoryProgress: {},
  totalPoints: 0,
  totalStars: 0,
  currentStreak: 0,
  longestStreak: 0,
  earnedBadges: [],
  lastPracticeDate: '',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

// Default rule progress template
const createDefaultRuleProgress = (ruleId: string): RuleProgress => ({
  ruleId,
  status: 'locked',
  stars: 0,
  gameScores: {
    soundToShape: 0,
    quickJudgment: 0,
    spelling: 0,
  },
  practiceCount: 0,
  correctCount: 0,
  lastPracticed: '',
});

class PhonicsProgressService {
  private progressCache: Map<string, PhonicsProgress> = new Map();

  /**
   * Get user's phonics progress
   */
  getProgress(userId: string): PhonicsProgress {
    // Check cache first
    if (this.progressCache.has(userId)) {
      return this.progressCache.get(userId)!;
    }

    // Load from localStorage
    try {
      const stored = localStorage.getItem(`${STORAGE_KEY}_${userId}`);
      if (stored) {
        const progress = JSON.parse(stored) as PhonicsProgress;
        this.progressCache.set(userId, progress);
        return progress;
      }
    } catch (error) {
      console.error('Failed to load phonics progress:', error);
    }

    // Create new progress
    const newProgress = createDefaultProgress(userId);
    this.saveProgress(newProgress);
    return newProgress;
  }

  /**
   * Save progress to storage
   */
  saveProgress(progress: PhonicsProgress): void {
    progress.updatedAt = new Date().toISOString();
    this.progressCache.set(progress.userId, progress);
    try {
      localStorage.setItem(`${STORAGE_KEY}_${progress.userId}`, JSON.stringify(progress));
    } catch (error) {
      console.error('Failed to save phonics progress:', error);
    }
  }

  /**
   * Initialize rule progress if not exists
   */
  initializeRuleProgress(userId: string, ruleId: string, isUnlocked: boolean): RuleProgress {
    const progress = this.getProgress(userId);

    if (!progress.ruleProgress[ruleId]) {
      progress.ruleProgress[ruleId] = {
        ...createDefaultRuleProgress(ruleId),
        status: isUnlocked ? 'unlocked' : 'locked',
      };
      this.saveProgress(progress);
    }

    return progress.ruleProgress[ruleId];
  }

  /**
   * Update rule progress after game completion
   */
  updateRuleProgress(
    userId: string,
    ruleId: string,
    result: GameResult
  ): RuleProgress {
    const progress = this.getProgress(userId);
    const ruleProgress = progress.ruleProgress[ruleId] || createDefaultRuleProgress(ruleId);

    // Update game scores
    if (result.score > ruleProgress.gameScores[result.gameType]) {
      ruleProgress.gameScores[result.gameType] = result.score;
    }

    // Update practice counts
    ruleProgress.practiceCount += 1;
    ruleProgress.correctCount += result.correct;
    ruleProgress.lastPracticed = new Date().toISOString();

    // Update status
    if (ruleProgress.status === 'unlocked') {
      ruleProgress.status = 'in-progress';
    }

    // Calculate stars based on accuracy
    const newStars = this.calculateStars(result.accuracy);
    if (newStars > ruleProgress.stars) {
      ruleProgress.stars = newStars;
      progress.totalStars += newStars - ruleProgress.stars;
    }

    // Check for completion (all 3 games played with good scores)
    const allGamesPlayed = Object.values(ruleProgress.gameScores).every(s => s > 0);
    const avgScore = Object.values(ruleProgress.gameScores).reduce((a, b) => a + b, 0) / 3;
    if (allGamesPlayed && avgScore >= 60 && ruleProgress.stars >= 1) {
      ruleProgress.status = 'completed';
      ruleProgress.completedAt = new Date().toISOString();
      if (ruleProgress.stars === 3) {
        ruleProgress.status = 'mastered';
      }
    }

    // Update total points
    progress.totalPoints += result.score;

    // Update streak
    this.updateStreak(progress);

    progress.ruleProgress[ruleId] = ruleProgress;
    this.saveProgress(progress);

    return ruleProgress;
  }

  /**
   * Calculate stars based on accuracy
   */
  private calculateStars(accuracy: number): 0 | 1 | 2 | 3 {
    if (accuracy >= 90) return 3;
    if (accuracy >= 70) return 2;
    if (accuracy >= 50) return 1;
    return 0;
  }

  /**
   * Update learning streak
   */
  private updateStreak(progress: PhonicsProgress): void {
    const today = new Date().toDateString();
    const lastPractice = progress.lastPracticeDate ? new Date(progress.lastPracticeDate).toDateString() : '';

    if (lastPractice === today) {
      // Already practiced today
      return;
    }

    const yesterday = new Date(Date.now() - 86400000).toDateString();
    if (lastPractice === yesterday) {
      // Continuing streak
      progress.currentStreak += 1;
      if (progress.currentStreak > progress.longestStreak) {
        progress.longestStreak = progress.currentStreak;
      }
    } else if (lastPractice !== today) {
      // Streak broken
      progress.currentStreak = 1;
    }

    progress.lastPracticeDate = new Date().toISOString();
  }

  /**
   * Check if a rule is unlocked
   */
  isRuleUnlocked(userId: string, ruleId: string, prerequisites: string[], minStars: number): boolean {
    const progress = this.getProgress(userId);

    // No prerequisites
    if (prerequisites.length === 0) {
      return true;
    }

    // Check all prerequisites
    for (const prereq of prerequisites) {
      const prereqProgress = progress.ruleProgress[prereq];
      if (!prereqProgress || prereqProgress.stars < minStars) {
        return false;
      }
    }

    return true;
  }

  /**
   * Unlock a rule
   */
  unlockRule(userId: string, ruleId: string): void {
    const progress = this.getProgress(userId);
    if (!progress.ruleProgress[ruleId]) {
      progress.ruleProgress[ruleId] = createDefaultRuleProgress(ruleId);
    }
    progress.ruleProgress[ruleId].status = 'unlocked';
    this.saveProgress(progress);
  }

  /**
   * Get overall progress percentage
   */
  getOverallProgress(userId: string, totalRules: number): number {
    const progress = this.getProgress(userId);
    const completedRules = Object.values(progress.ruleProgress)
      .filter(r => r.status === 'completed' || r.status === 'mastered').length;
    return Math.round((completedRules / totalRules) * 100);
  }

  /**
   * Get category progress
   */
  getCategoryProgress(userId: string, categoryId: string, totalRules: number): CategoryProgress {
    const progress = this.getProgress(userId);

    if (!progress.categoryProgress[categoryId]) {
      progress.categoryProgress[categoryId] = {
        categoryId,
        rulesCompleted: 0,
        totalRules,
        totalStars: 0,
        maxStars: totalRules * 3,
        isUnlocked: categoryId === 'short-vowels', // First category is unlocked
      };
    }

    return progress.categoryProgress[categoryId];
  }

  /**
   * Check and award badges
   */
  checkAndAwardBadges(userId: string): PhonicsBadge[] {
    const progress = this.getProgress(userId);
    const newBadges: PhonicsBadge[] = [];
    const earnedIds = new Set(progress.earnedBadges.map(b => b.id));

    // Define badge requirements
    const badges = [
      {
        id: 'first-rule',
        name: '入门者',
        icon: '🌱',
        description: '完成第一个规则学习',
        category: 'mastery' as const,
        check: () => Object.values(progress.ruleProgress).filter(r => r.status === 'completed' || r.status === 'mastered').length >= 1,
      },
      {
        id: 'five-rules',
        name: '初学者',
        icon: '📚',
        description: '完成5个规则学习',
        category: 'mastery' as const,
        check: () => Object.values(progress.ruleProgress).filter(r => r.status === 'completed' || r.status === 'mastered').length >= 5,
      },
      {
        id: 'streak-7',
        name: '周周坚持',
        icon: '📅',
        description: '连续学习7天',
        category: 'streak' as const,
        check: () => progress.currentStreak >= 7,
      },
      {
        id: 'streak-30',
        name: '月度冠军',
        icon: '👑',
        description: '连续学习30天',
        category: 'streak' as const,
        check: () => progress.currentStreak >= 30,
      },
      {
        id: 'points-500',
        name: '积少成多',
        icon: '⭐',
        description: '累计获得500分',
        category: 'mastery' as const,
        check: () => progress.totalPoints >= 500,
      },
      {
        id: 'points-2000',
        name: '积分达人',
        icon: '🌟',
        description: '累计获得2000分',
        category: 'mastery' as const,
        check: () => progress.totalPoints >= 2000,
      },
    ];

    for (const badge of badges) {
      if (!earnedIds.has(badge.id) && badge.check()) {
        const newBadge: PhonicsBadge = {
          id: badge.id,
          name: badge.name,
          icon: badge.icon,
          description: badge.description,
          category: badge.category,
          earnedAt: new Date().toISOString(),
        };
        newBadges.push(newBadge);
        progress.earnedBadges.push(newBadge);
      }
    }

    if (newBadges.length > 0) {
      this.saveProgress(progress);
    }

    return newBadges;
  }

  /**
   * Reset progress for a user
   */
  resetProgress(userId: string): void {
    const newProgress = createDefaultProgress(userId);
    this.saveProgress(newProgress);
  }
}

export const phonicsProgressService = new PhonicsProgressService();
